/**
 * Seed data for local development mode.
 * Creates a realistic studio setup from the shared device preset library
 * so the canvas has something to render without needing the API, and runs
 * the same rule-based routing engine the Auto-Map button uses to pre-wire
 * the devices into a plausible signal flow. Without this, the demo at
 * /editor/local-dev looks like a parts bin — the visitor has to know to
 * click Auto-Map before anything interesting happens.
 */
import { Device, Connection } from '@/types/devices'
import { DEVICE_PRESETS } from '@busswerks/shared'
import { expandPresetPorts } from './presetToDevice'
import { generateSuggestions } from '@/engine/autoMapper'

/**
 * Names of the devices to include in the seed layout, mapped to their
 * exact name in the shared preset library. Chosen to feel like gear a
 * typical home / project studio visitor would recognise, while keeping
 * every category the auto-mapper routes (computer, controller, source,
 * mixer, effects, interface, monitor controller, monitors).
 */
const SEED_DEVICE_NAMES = [
  'MacBook Pro (14"/16")',
  'Ableton Push 3',
  'ASM Hydrasynth 49',
  'Roland TR-8S',
  'Mackie 2404 VLZ4',
  'Eventide H90',
  'Arturia AudioFuse 16Rig',
  'Mackie Big Knob Passive',
  'ADAM Audio A8H',
]

/**
 * Default canvas positions for seed devices.
 * Laid out roughly left-to-right by signal flow:
 *   computer / controller on top, sources on the left, mixer + effects
 *   in the middle, interface → monitor controller → monitors on the right.
 */
const SEED_POSITIONS: Record<string, { x: number; y: number }> = {
  'MacBook Pro (14"/16")': { x: 700, y: -180 },
  'Ableton Push 3': { x: 1050, y: -180 },
  'ASM Hydrasynth 49': { x: 100, y: 50 },
  'Roland TR-8S': { x: 100, y: 400 },
  'Mackie 2404 VLZ4': { x: 500, y: 100 },
  'Eventide H90': { x: 500, y: 550 },
  'Arturia AudioFuse 16Rig': { x: 900, y: 100 },
  'Mackie Big Knob Passive': { x: 1300, y: 100 },
  'ADAM Audio A8H': { x: 1650, y: 100 },
}

export function createSeedDevices(): Device[] {
  let portIdCounter = 0
  const nextPortId = () => `port-${++portIdCounter}`

  return SEED_DEVICE_NAMES.map((name, idx) => {
    const preset = DEVICE_PRESETS.find(p => p.name === name)
    if (!preset) {
      console.warn(`Seed device preset not found: "${name}"`)
      return {
        id: `dev-${idx + 1}`,
        name,
        category: 'interface' as Device['category'],
        inputs: [],
        outputs: [],
        position: SEED_POSITIONS[name] || { x: idx * 300, y: 100 },
      }
    }

    const { inputs, outputs } = expandPresetPorts(preset, nextPortId)

    return {
      id: `dev-${idx + 1}`,
      name: preset.name,
      manufacturer: preset.manufacturer,
      category: preset.category as Device['category'],
      inputs,
      outputs,
      position: SEED_POSITIONS[name] || { x: idx * 300, y: 100 },
    }
  })
}

/**
 * Run the same auto-mapper the Auto-Map button uses so the demo canvas
 * loads already wired up. IDs are deterministic (`seed-conn-${i}`) so
 * the layout round-trips through localStorage without regenerating
 * connection rows on each reload.
 */
export function createSeedConnections(devices: Device[]): Connection[] {
  const suggestions = generateSuggestions(devices, [])
  return suggestions.map((s, i) => ({
    id: `seed-conn-${i + 1}`,
    sourceDeviceId: s.sourceDevice.id,
    sourcePortId: s.sourcePort.id,
    targetDeviceId: s.targetDevice.id,
    targetPortId: s.targetPort.id,
    signalType: s.signalType,
  }))
}

export function createSeedLayout() {
  const now = new Date().toISOString()
  const devices = createSeedDevices()
  const connections = createSeedConnections(devices)
  return {
    id: 'local-dev',
    name: 'Dev Studio Setup',
    description: 'Local development layout with seed data',
    devices,
    connections,
    zones: [],
    version: 1,
    createdAt: now,
    updatedAt: now,
    ownerId: 'local-dev-user',
  }
}
